import type { BaseClient } from '../../http/BaseClient.js';
import type {
  AcquireScanLeaseRequest,
  AcquireScanLeaseResponse,
  ListScanHistoryQuery,
  ListScanHistoryResponse,
  PeekScanLeaseResponse,
  ResourceTreeQuery,
  ResourceTreeResponse,
  SubgraphRequest,
  SubgraphResponse,
} from './types.js';

export class ResourcesClient {
  constructor(private readonly http: BaseClient) {}

  getTree(repoId: string, query?: ResourceTreeQuery): Promise<ResourceTreeResponse> {
    const params = new URLSearchParams();
    const search = query?.search?.trim();
    if (search) params.set('search', search);
    if (query?.showDefault === true) params.set('showDefault', 'true');
    const qs = params.size > 0 ? `?${params.toString()}` : '';
    return this.http.get<ResourceTreeResponse>(`/repos/${repoId}/resources/tree${qs}`);
  }

  /**
   * BFS-walks the dependency graph starting from `seedResourceIds` (or
   * `seedNameTokens`) and returns the matching nodes + edges. Use this
   * to render an inbound/outbound dependency view for a selected
   * resource. The server caps the result at maxNodes (default 80,
   * hard cap 200) and sets `truncated` when the cap was hit.
   */
  getSubgraph(repoId: string, request: SubgraphRequest): Promise<SubgraphResponse> {
    return this.http.post<SubgraphResponse>(`/repos/${repoId}/resources/subgraph`, { body: request });
  }

  /**
   * Returns recent scan history for a repo, optionally filtered by
   * credential. Backed by CloudScanHistory rows written when the
   * agent's discovery orchestrator releases a lease (or when the
   * backend's cleanup job sweeps an expired one). Use this to render
   * "last refreshed N min ago" badges and per-scope add/remove counts
   * next to the resource tree.
   */
  listScans(repoId: string, query?: ListScanHistoryQuery): Promise<ListScanHistoryResponse> {
    const params = new URLSearchParams();
    if (query?.credentialId) params.set('credentialId', query.credentialId);
    if (query?.limit != null) params.set('limit', String(query.limit));
    const qs = params.size > 0 ? `?${params.toString()}` : '';
    return this.http.get<ListScanHistoryResponse>(`/repos/${repoId}/scans${qs}`);
  }

  /**
   * Peeks the active scan lease (if any) for the given credential. Used
   * by the dashboard to display a "scan in progress" badge without
   * attempting to acquire the lease. Returns `lease: null` when no
   * scan is running. Expired leases are returned with their stale
   * timestamps — callers should compare `expiresAt` to `Date.now()`
   * before treating them as active.
   */
  peekScanLease(repoId: string, credentialId: string): Promise<PeekScanLeaseResponse> {
    const params = new URLSearchParams();
    params.set('credentialId', credentialId);
    return this.http.get<PeekScanLeaseResponse>(`/repos/${repoId}/scans/lease?${params.toString()}`);
  }

  /**
   * Attempts to claim a scan lease. Returns the acquired lease on
   * success. The server responds 409 with a `ScanInProgressError`-
   * shaped body when another scan is already in flight — callers
   * should catch the HttpError, parse the body, and surface a polite
   * "another scan is running" notice rather than retry automatically.
   *
   * NOTE: routine use is the agent's job. The dashboard typically only
   * calls peekScanLease + listScans; this method is exposed for power-
   * user tooling and automated test harnesses.
   */
  acquireScanLease(repoId: string, request: AcquireScanLeaseRequest): Promise<AcquireScanLeaseResponse> {
    return this.http.post<AcquireScanLeaseResponse>(`/repos/${repoId}/scans/lease`, { body: request });
  }
}
